<template>
  <div class="text-underline-dialog">
    <v-form ref="form" v-model="isFormValid">
      <v-row>
        <v-col cols="12">
          <VaTinymceInput
            ref="labelEditor"
            title="Text"
            required
            :display-errors="displayErrors"
            :text-only-with-line-height="true"
            :editor-height="200"
            :current-content="formData.text"
            @update="val => (formData.text = val)" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <VaColorPicker
            :position="'bottom'"
            :title="'Line color'"
            v-model="formData.lineColor"></VaColorPicker>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6">
          <v-text-field
            v-model.number="formData.lineWeight"
            label="Line weight (px)"
            type="number"
            min="1"
            max="40"
            :rules="[v => (v >= 1 && v <= 40) || 'Line weight must be between 1-40px']"
            suffix="px"
            persistent-placeholder />
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model.number="formData.lineLength"
            label="Line length (px)"
            type="number"
            min="1"
            max="1200"
            :rules="[v => (v >= 1 && v <= 1200) || 'Line length must be between 1-1200px']"
            suffix="px"
            persistent-placeholder />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-select v-model="formData.alignment" :items="alignmentOptions" label="Alignment" persistent-placeholder />
        </v-col>
      </v-row>

      <v-alert v-if="errorMessage" type="error" class="mb-4" dismissible @input="errorMessage = ''">
        {{ errorMessage }}
      </v-alert>
    </v-form>
  </div>
</template>

<script>
  import cloneDeep from "lodash/cloneDeep";
  import { TEXT_UNDERLINE_EVENT_TYPES } from "@/constant";
  import VaColorPicker from "@/components/custom/VaColorPicker.vue";
  import VaTinymceInput from "@/components/custom/VaTinymceInput.vue";

  const DEFAULT_FORM_DATA = {
    text: '<p style="font-size: 12px; color: #2454C7;">BENEFIT #1</p>',
    lineColor: "#2454C7",
    lineWeight: 2,
    lineLength: 140,
    alignment: "left",
  };

  const isNonEmptyHtml = html => {
    if (!html) return false;
    return (
      html
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "")
        .trim().length > 0
    );
  };

  export default {
    name: "TextUnderlineDialog",
    components: {
      VaColorPicker,
      VaTinymceInput,
    },
    data() {
      return {
        isFormValid: false,
        displayErrors: false,
        errorMessage: "",
        formData: cloneDeep(DEFAULT_FORM_DATA),
        alignmentOptions: [
          { text: "Left", value: "left" },
          { text: "Center", value: "center" },
          { text: "Right", value: "right" },
        ],
      };
    },
    computed: {
      isAllValid() {
        return this.isFormValid && isNonEmptyHtml(this.formData.text);
      },
    },
    watch: {
      isAllValid: {
        handler() {
          this.sendValidation();
        },
      },
      formData: {
        handler() {
          this.validateAndUpdate();
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    methods: {
      setupReceiveDataListener() {
        window.onmessage = async message => {
          if (message.data.type === TEXT_UNDERLINE_EVENT_TYPES.NEED_VALIDATION) {
            this.displayErrors = true;
            await this.$refs.form.validate();
            this.errorMessage = this.isAllValid ? "" : "Please fill in the required fields";
          }
          if (message.data.type === TEXT_UNDERLINE_EVENT_TYPES.SEND_DATA) {
            const { data } = message.data;
            if (data === "undefined" || !data || Object.keys(data).length === 0) {
              this.updateValue();
            } else {
              this.formData = { ...DEFAULT_FORM_DATA, ...data };
              this.$nextTick(() => {
                this.$refs.labelEditor?.updateContent(this.formData.text);
              });
            }
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: TEXT_UNDERLINE_EVENT_TYPES.REQUEST_DATA,
          },
          "*",
        );
      },
      async validateAndUpdate() {
        if (this.$refs.form) {
          await this.$refs.form.validate();
          if (this.isAllValid) {
            this.updateValue();
            this.errorMessage = "";
          }
        }
      },
      updateValue() {
        window.parent.postMessage(
          {
            mceAction: TEXT_UNDERLINE_EVENT_TYPES.UPDATE_TEXT_UNDERLINE_DATA,
            data: cloneDeep(this.formData),
          },
          "*",
        );
      },
      sendValidation() {
        window.parent.postMessage(
          {
            mceAction: TEXT_UNDERLINE_EVENT_TYPES.VALIDATION,
            data: {
              isValid: this.isAllValid,
            },
          },
          "*",
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  .text-underline-dialog {
    padding: 20px;

    .row {
      margin-top: 0;
      margin-bottom: 0;
    }

    .col {
      padding-top: 4px;
      padding-bottom: 4px;
    }

    ::v-deep .v-text-field__suffix {
      padding-right: 6px;
    }
  }
</style>
